/**
 * Free-zone business-setup catalog schemas (OPS 3.3) — Admin-managed tree of
 * service → free zone → sub-item checklist. Engagement business-setup
 * sub-items are spawned from it (or added manually) and feed ONE_OFF
 * proposals as snapshot sources.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema } from './common';

export const freeZoneCatalogItemOutputSchema = z.object({
  id: idSchema,
  freeZoneId: idSchema,
  service: z.string().max(100),
  label: z.string().max(200),
  ordering: z.number().int(),
  active: z.boolean(),
});
export type FreeZoneCatalogItemOutput = z.infer<typeof freeZoneCatalogItemOutputSchema>;

export const freeZoneOutputSchema = z.object({
  id: idSchema,
  code: z.string().max(20),
  name: z.string().max(100),
  active: z.boolean(),
  ordering: z.number().int(),
  items: z.array(freeZoneCatalogItemOutputSchema),
});
export type FreeZoneOutput = z.infer<typeof freeZoneOutputSchema>;

export const createFreeZoneInputSchema = z.object({
  code: z.string().min(1).max(20).regex(/^[A-Z0-9_]+$/, 'Uppercase letters, digits and underscores only'),
  name: z.string().min(1).max(100),
  ordering: z.number().int().min(0).optional(),
});
export type CreateFreeZoneInput = z.infer<typeof createFreeZoneInputSchema>;

export const updateFreeZoneInputSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  active: z.boolean().optional(),
  ordering: z.number().int().min(0).optional(),
});
export type UpdateFreeZoneInput = z.infer<typeof updateFreeZoneInputSchema>;

export const createFreeZoneItemInputSchema = z.object({
  service: z.string().min(1).max(100),
  label: z.string().min(1).max(200),
  ordering: z.number().int().min(0).optional(),
});
export type CreateFreeZoneItemInput = z.infer<typeof createFreeZoneItemInputSchema>;

export const updateFreeZoneItemInputSchema = createFreeZoneItemInputSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateFreeZoneItemInput = z.infer<typeof updateFreeZoneItemInputSchema>;

// ── Engagement sub-items ─────────────────────────────────────────────────────

/** OPS 3.7 step 2: execution checklist states (was a free varchar with
 *  PENDING/IN_PROGRESS/DONE; the migration maps IN_PROGRESS→IN_PROCESS and
 *  DONE→COMPLETED). */
export const engagementSubItemStatusSchema = z.enum(['PENDING', 'IN_PROCESS', 'COMPLETED']);
export type EngagementSubItemStatus = z.infer<typeof engagementSubItemStatusSchema>;

export const engagementSubItemOutputSchema = z.object({
  id: idSchema,
  engagementId: idSchema,
  catalogItemId: idSchema.nullable(),
  title: z.string().max(200),
  service: z.string().max(100).nullable(),
  freeZoneCode: z.string().max(20).nullable(),
  status: engagementSubItemStatusSchema,
  notes: z.string().max(2000).nullable(),
  ordering: z.number().int(),
  completedAt: iso8601Schema.nullable(),
  createdAt: iso8601Schema,
});
export type EngagementSubItemOutput = z.infer<typeof engagementSubItemOutputSchema>;

export const createEngagementSubItemInputSchema = z.object({
  title: z.string().min(1).max(200),
  service: z.string().max(100).nullish(),
  freeZoneCode: z.string().max(20).nullish(),
  ordering: z.number().int().min(0).optional(),
});
export type CreateEngagementSubItemInput = z.infer<typeof createEngagementSubItemInputSchema>;

export const updateEngagementSubItemInputSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  status: engagementSubItemStatusSchema.optional(),
  notes: z.string().max(2000).nullish(),
  ordering: z.number().int().min(0).optional(),
});
export type UpdateEngagementSubItemInput = z.infer<typeof updateEngagementSubItemInputSchema>;

/** Spawn sub-items from the catalog; idempotent per catalog item. */
export const applyFreeZoneCatalogInputSchema = z.object({
  freeZoneId: idSchema,
  /** Narrow to one service group; defaults to every active item in the zone. */
  service: z.string().max(100).nullish(),
  /**
   * OPS 3.5 C checklist parity with the compliance-template apply: when
   * present, only these catalog items spawn (they must belong to the zone and
   * match the service filter). Omitting keeps the whole-set behavior.
   */
  catalogItemIds: z.array(idSchema).min(1).max(200).optional(),
});
export type ApplyFreeZoneCatalogInput = z.infer<typeof applyFreeZoneCatalogInputSchema>;
